import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AREA_COLORS, type Area, type AreaColor, type AreaDraft } from '../../domain/area'
import { CheckIcon, CloseIcon } from './Icons'
import { useI18n } from '../../i18n'

interface AreaSheetProps {
  area: Area | null
  open: boolean
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: AreaDraft, specializationNames: string[]) => Promise<boolean>
  onDelete?: () => Promise<boolean>
}

export const AreaSheet = ({ area, open, saving, error, onClose, onSave, onDelete }: AreaSheetProps) => {
  const { copy: { common, areaSheet } } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [color, setColor] = useState<AreaColor>(AREA_COLORS[0])
  const [selectedSpecializations, setSelectedSpecializations] = useState<Set<string>>(new Set())
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    if (!open) return
    setName(area?.name ?? '')
    setColor(area?.color ?? AREA_COLORS[0])
    setSelectedSpecializations(new Set())
    setConfirmDelete(false)
  }, [area, open])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return
    await onSave({ name: name.trim(), color }, [...selectedSpecializations])
  }

  return (
    <dialog ref={dialogRef} className="sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <form className="sheet__body" onSubmit={submit}>
        <div className="sheet__handle" />
        <div className="sheet__heading">
          <div>
            <h2>{area ? areaSheet.edit : areaSheet.create}</h2>
            <p>{areaSheet.prompt}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={common.close}>
            <CloseIcon />
          </button>
        </div>

        <label className="field">
          <span>{common.name}</span>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder={areaSheet.example} maxLength={60} autoFocus />
        </label>

        {!area && (
          <fieldset className="form-section area-specialization-suggestions">
            <legend>{areaSheet.skillsOptional}</legend>
            <p>{areaSheet.skillsHelp}</p>
            <div className="category-chips">
              {areaSheet.suggestions.map((specialization) => {
                const selected = selectedSpecializations.has(specialization)
                return (
                  <button
                    key={specialization}
                    className={selected ? 'category-chip category-chip--selected' : 'category-chip'}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedSpecializations((current) => {
                      const next = new Set(current)
                      if (next.has(specialization)) next.delete(specialization)
                      else next.add(specialization)
                      return next
                    })}
                  >
                    {selected && <CheckIcon />}{specialization}
                  </button>
                )
              })}
            </div>
            <small>{areaSheet.skip}</small>
          </fieldset>
        )}

        <p className="sheet__note">{areaSheet.later}</p>
        <fieldset className="color-picker">
          <legend>{areaSheet.color}</legend>
          <div>
            {AREA_COLORS.map((option) => (
              <button
                key={option}
                className={color === option ? 'color-option color-option--selected' : 'color-option'}
                type="button"
                style={{ backgroundColor: option }}
                onClick={() => setColor(option)}
                aria-label={`${areaSheet.chooseColor} ${option}`}
                aria-pressed={color === option}
              >
                {color === option && <CheckIcon />}
              </button>
            ))}
          </div>
        </fieldset>

        {error && <div className="error-message" role="alert">{error}</div>}

        {area && onDelete && (
          confirmDelete ? (
            <div className="delete-confirm">
              <p>{common.delete} “{area.name}”? {areaSheet.deleteConfirm}</p>
              <div>
                <button type="button" className="button button--ghost" onClick={() => setConfirmDelete(false)}>{common.cancel}</button>
                <button type="button" className="button button--danger" disabled={saving} onClick={() => void onDelete()}>{common.delete}</button>
              </div>
            </div>
          ) : (
            <button type="button" className="delete-link" disabled={saving} onClick={() => setConfirmDelete(true)}>{areaSheet.deleteArea}</button>
          )
        )}

        <button className="button button--primary" type="submit" disabled={!name.trim() || saving}>
          {saving ? common.saving : area ? common.saveChanges : areaSheet.createArea}
        </button>
      </form>
    </dialog>
  )
}
